/**
 * <price-tooltip> — small popovers anchored to a price row (not a modal).
 * Markup comes from snippets/price-tooltip.liquid.
 *
 * Each trigger button controls its own panel (aria-controls):
 * - (i) icon: opens on hover (hover-capable devices) and toggles on click / tap.
 * - "Price History" link: toggles its own panel on click / tap (no hover).
 * Only one panel is open at a time. Panels close on outside tap/click, Esc and when focus leaves
 * the component. Each panel is placed under its own trigger and kept inside the viewport
 * (shifted horizontally, arrow follows the trigger), so it fits 360px screens.
 */
class PriceTooltip extends HTMLElement {
  /** @type {{ trigger: HTMLButtonElement, panel: HTMLElement }[]} */
  #items = [];
  #hoverTimer = 0;
  #hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

  connectedCallback() {
    this.#items = [];
    this.querySelectorAll('.price-tooltip__trigger').forEach((trigger) => {
      const panel = document.getElementById(trigger.getAttribute('aria-controls') || '');
      if (!panel || !this.contains(panel)) return;
      this.#items.push({ trigger: /** @type {HTMLButtonElement} */ (trigger), panel });

      trigger.addEventListener('click', this.#onTriggerClick);
      // Hover opens only the (i) icon panel; the "Price History" link opens on click.
      if (trigger.classList.contains('price-tooltip__trigger--icon')) {
        trigger.addEventListener('mouseenter', this.#onIconEnter);
        trigger.addEventListener('mouseleave', this.#onIconLeave);
        panel.addEventListener('mouseenter', this.#onPanelEnter);
        panel.addEventListener('mouseleave', this.#onIconLeave);
      }
    });
    if (!this.#items.length) return;

    this.addEventListener('focusout', this.#onFocusOut);
    document.addEventListener('keydown', this.#onKeyDown);
    document.addEventListener('pointerdown', this.#onDocumentPointerDown);
    window.addEventListener('resize', this.#onResize);
  }

  disconnectedCallback() {
    clearTimeout(this.#hoverTimer);
    document.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown);
    window.removeEventListener('resize', this.#onResize);
  }

  /** @param {{ trigger: HTMLButtonElement, panel: HTMLElement }} item */
  #isOpen(item) {
    return !item.panel.hidden;
  }

  get isOpen() {
    return this.#items.some((item) => this.#isOpen(item));
  }

  /** @param {{ trigger: HTMLButtonElement, panel: HTMLElement }} item */
  #open(item) {
    this.#items.forEach((other) => {
      if (other !== item) this.#close(other);
    });
    if (this.#isOpen(item)) return;
    item.panel.hidden = false;
    item.trigger.setAttribute('aria-expanded', 'true');
    this.#position(item);
  }

  /** @param {{ trigger: HTMLButtonElement, panel: HTMLElement }} item */
  #close(item) {
    if (!this.#isOpen(item)) return;
    item.panel.hidden = true;
    item.trigger.setAttribute('aria-expanded', 'false');
  }

  closeAll() {
    this.#items.forEach((item) => this.#close(item));
  }

  /**
   * Put the panel under its trigger, shift it so it never leaves the viewport (360px screens)
   * and point the arrow at the trigger centre.
   * @param {{ trigger: HTMLButtonElement, panel: HTMLElement }} item
   */
  #position({ trigger, panel }) {
    const margin = 16;
    panel.style.setProperty('--tt-left', `${trigger.offsetLeft}px`);
    panel.style.setProperty('--tt-shift', '0px');
    const panelRect = panel.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;

    let shift = 0;
    if (panelRect.right > viewportWidth - margin) shift = viewportWidth - margin - panelRect.right;
    if (panelRect.left + shift < margin) shift = margin - panelRect.left;
    panel.style.setProperty('--tt-shift', `${Math.round(shift)}px`);

    const triggerRect = trigger.getBoundingClientRect();
    const triggerCenter = triggerRect.left + triggerRect.width / 2;
    const arrowLeft = Math.min(Math.max(triggerCenter - (panelRect.left + shift) - 7, 12), panelRect.width - 26);
    panel.style.setProperty('--tt-arrow-left', `${Math.round(arrowLeft)}px`);
  }

  /** @param {Event} event */
  #itemFor(event) {
    const target = /** @type {Element} */ (event.currentTarget);
    return this.#items.find((item) => item.trigger === target || item.panel === target);
  }

  #onTriggerClick = (/** @type {Event} */ event) => {
    event.preventDefault();
    event.stopPropagation();
    const item = this.#itemFor(event);
    if (!item) return;
    if (this.#isOpen(item)) this.#close(item);
    else this.#open(item);
  };

  #onIconEnter = (/** @type {Event} */ event) => {
    if (!this.#hoverQuery.matches) return;
    clearTimeout(this.#hoverTimer);
    const item = this.#itemFor(event);
    if (item) this.#open(item);
  };

  #onPanelEnter = () => {
    clearTimeout(this.#hoverTimer);
  };

  #onIconLeave = (/** @type {Event} */ event) => {
    if (!this.#hoverQuery.matches) return;
    const item = this.#itemFor(event);
    if (!item) return;
    clearTimeout(this.#hoverTimer);
    this.#hoverTimer = window.setTimeout(() => this.#close(item), 150);
  };

  #onFocusOut = (/** @type {FocusEvent} */ event) => {
    const next = /** @type {Node | null} */ (event.relatedTarget);
    if (next && this.contains(next)) return;
    this.closeAll();
  };

  #onKeyDown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key !== 'Escape') return;
    const openItem = this.#items.find((item) => this.#isOpen(item));
    if (!openItem) return;
    this.closeAll();
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !this.contains(active)) openItem.trigger.focus();
  };

  #onDocumentPointerDown = (/** @type {Event} */ event) => {
    if (!this.isOpen) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.closeAll();
  };

  #onResize = () => {
    this.#items.forEach((item) => {
      if (this.#isOpen(item)) this.#position(item);
    });
  };
}

if (!customElements.get('price-tooltip')) {
  customElements.define('price-tooltip', PriceTooltip);
}
